import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { APP_DISPLAY_NAME } from '@mcikm-vote/shared';

import { Logo } from '../components/Logo';
import { RichText } from '../components/RichText';
import { ThemeToggle } from '../components/ThemeToggle';
import { usePersonalisierung } from '../personalisierung/personalisierungApi';

/**
 * Öffentliche Seite für Impressum und Datenschutz (Änderungsanforderung ÄA-5).
 *
 * Erreichbar **ohne Anmeldung** unter `/impressum` und `/datenschutz`. Die Fußzeile verweist
 * darauf und öffnet die Seite in einem neuen Tab — so verlässt ein versehentlicher Klick keine
 * laufende Abstimmung (§19.1).
 *
 * **Mit Kopfzeile wie der Rest der Anwendung.** Eine Rechtstextseite ist oft die erste Seite, die
 * ein Besucher von der Anwendung zu sehen bekommt — sie wird aus der Fußzeile heraus in einem
 * eigenen Tab geöffnet und steht damit für sich. Ohne Kopf stünde dort eine nackte Textwüste. Die
 * Leiste trägt dasselbe Logo wie Kopfzeile und Anmeldeseiten, also bei hinterlegtem Firmenlogo das
 * des Betreibers (siehe {@link Logo}), und die Umschaltung des Erscheinungsbilds nach §21.
 *
 * Das Logo führt zur Startseite. Wer die Seite über einen geteilten Verweis erreicht, kommt damit
 * in die Anwendung; wer aus der Fußzeile kam, hat seinen Ursprungstab ohnehin noch offen.
 *
 * Ist nichts hinterlegt, sagt die Seite das ausdrücklich. Ein stiller Rückfall auf eine fremde
 * Adresse wäre schlechter: Der Betreiber bemerkte nicht, dass sein Impressum fehlt.
 */
export function RechtstextSeite({ art }: { art: 'impressum' | 'datenschutz' }): ReactElement {
  const personalisierung = usePersonalisierung();
  const titel = art === 'impressum' ? 'Impressum' : 'Datenschutz';

  const inhalt =
    art === 'impressum'
      ? personalisierung.data?.impressumMarkdown
      : personalisierung.data?.datenschutzMarkdown;

  return (
    <div className="flex flex-1 flex-col">
      <header className="border-rand bg-flaeche flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
        {/* Der Verweis trägt den Namen: Ein eigenes Firmenlogo hat bewusst keinen
            Alternativtext, das Ziel bliebe für Vorlesewerkzeuge sonst unbenannt (§19.1). */}
        <Link to="/" aria-label={`Zur Startseite von ${APP_DISPLAY_NAME}`}>
          <Logo />
        </Link>

        <ThemeToggle />
      </header>

      <main className="mx-auto w-full max-w-3xl flex-1 p-4 sm:p-6">
        <h1 className="mb-4 text-2xl font-semibold">{titel}</h1>

        {personalisierung.isPending ? (
          <p className="text-text-gedaempft">Wird geladen …</p>
        ) : inhalt === undefined || inhalt.trim() === '' ? (
          <p className="text-text-gedaempft">
            Es ist noch {art === 'impressum' ? 'kein Impressum' : 'keine Datenschutzerklärung'}{' '}
            hinterlegt.
          </p>
        ) : (
          <RichText markdown={inhalt} />
        )}
      </main>
    </div>
  );
}
